import {
  TelegramAvailableFeaturesControllerFindAll200,
  TelegramTransactionsPaymentsSystemsResponseDto,
  TelegramTransactionsUserControllerGetTopUpVariants200,
} from '@/generated';
import { assign, createMachine, sendTo } from 'xstate';
import { Dict } from './types';
import { createFeatureCheckoutFlow } from './checkoutFlow';
import { getFeaturePriceBreakdown } from '@/utils/feature-price';

/** ----- Types ----- */

export interface FeaturePurchaseContext {
  payments: TelegramTransactionsPaymentsSystemsResponseDto[];
  availableAmounts: TelegramTransactionsUserControllerGetTopUpVariants200;
  productId: string;
  coinsBalance: number;
  pendingPaymentSystem?: string;
  /** Provided at start (input) */
  features: TelegramAvailableFeaturesControllerFindAll200;
  /** UI grouping order (derived from features' type) */
  typesOrder: string[];
  /** Index of the current type tab/screen */
  typeIndex: number;
  /** Selected feature ids */
  selected: string[];
  /** Cached totals (recomputed on enter summary or checkout when skipSummary) */
  coinsCost: number;
  fiatCost: number;
  fiatFakeCost: number;
  telegramStarsAmount: number;
  paidPaymentSystem?: string;
  dict: Dict;
  skipSummary: boolean;
}

export type FeaturePurchaseEvents =
  | { type: 'TOGGLE_FEATURE'; id: string }
  | { type: 'NEXT_PAGE' }
  | { type: 'PREV_PAGE' }
  | { type: 'BACK_TO_SUMMARY' }
  | { type: 'BACK_TO_SELECTION' }
  | { type: 'SELECT_METHOD'; paymentSystem: string }
  | { type: 'GO_PAY_CARD_COM'; parts: number; payerName: string; token?: string }
  | { type: 'NEED_MORE_COINS' }
  | { type: 'MANUAL_TRANSFER' }
  | { type: 'RESET' };

export type FeaturePurchaseInput = {
  features: TelegramAvailableFeaturesControllerFindAll200;
  payments: TelegramTransactionsPaymentsSystemsResponseDto[];
  availableAmounts: TelegramTransactionsUserControllerGetTopUpVariants200;
  coinsBalance: number;
  productId: string;
  /** Optional: custom ordering of types. If omitted, derived from features */
  typesOrder?: string[];
  /** Skip the summary screen and go directly from selection to checkout */
  skipSummary?: boolean;
  /** Override initial selection; if omitted, first TopUp is pre-selected */
  initialSelected?: string[];
};

export type FeaturePurchaseOutput = {
  selected: TelegramAvailableFeaturesControllerFindAll200[];
  total: number;
};

function deriveTypesOrder(features: TelegramAvailableFeaturesControllerFindAll200, provided?: string[]): string[] {
  if (provided && provided.length) return provided;
  const order: string[] = [];
  for (const f of features) {
    if (!order.includes(f.type)) order.push(f.type);
  }
  return order;
}

type GetArrValue<T extends Array<any>> = T extends Array<infer V> ? V : never;

export function featuresForType(
  features: TelegramAvailableFeaturesControllerFindAll200,
  type: string,
): GetArrValue<TelegramAvailableFeaturesControllerFindAll200> {
  return features.find((f) => f.type === type)!;
}

function findFirstFeatureTopUp(features: TelegramAvailableFeaturesControllerFindAll200): string {
  for (const f of features) {
    if (f.type === 'TopUp') {
      return f.values[0].id;
    }
  }

  return '';
}

function createDictFeatures(features: TelegramAvailableFeaturesControllerFindAll200): Dict {
  const dict: Dict = {};

  for (const f of features) {
    if (f.type === 'TopUp') {
      for (const subf of f.values) {
        dict[subf.id] = { ...subf, type: 'TopUp' };
      }
      continue;
    }

    dict[f.id] = f;
  }

  return dict;
}

type FeatureTotals = Pick<FeaturePurchaseContext, 'coinsCost' | 'fiatCost' | 'fiatFakeCost' | 'telegramStarsAmount'>;

function computeTotal(selectedIds: string[], dict: Dict): FeatureTotals {
  let coinsCost = 0;
  let fiatCost = 0;
  let fiatFakeCost = 0;
  let telegramStarsAmount = 0;

  for (const id of selectedIds) {
    const price = getFeaturePriceBreakdown(dict[id]);
    coinsCost += price.coinsAmount;
    fiatCost += price.fiatAmount;
    fiatFakeCost += price.fiatFakeAmount > 0 ? price.fiatFakeAmount : price.fiatAmount;
    telegramStarsAmount += price.telegramStarsAmount;
  }

  return {
    coinsCost,
    fiatCost,
    fiatFakeCost,
    telegramStarsAmount,
  };
}

type onSuccess = (prodId: string) => void;

/** ----- Factory ----- */
export function createFeaturePurchaseSubflow(onSuccessPay: onSuccess) {
  return createMachine({
    types: {} as {
      context: FeaturePurchaseContext;
      events: FeaturePurchaseEvents;
      input: FeaturePurchaseInput;
      output: FeaturePurchaseOutput;
    },
    id: 'featurePurchaseSubflow',
    context: ({ input }) => ({
      features: input.features ?? [],
      typesOrder: deriveTypesOrder(input.features ?? [], input.typesOrder),
      productId: input.productId,
      payments: input.payments,
      availableAmounts: input.availableAmounts,
      coinsBalance: input.coinsBalance,
      pendingPaymentSystem: undefined,
      typeIndex: 0,
      selected:
        input.initialSelected !== undefined ? input.initialSelected : [findFirstFeatureTopUp(input.features || [])],
      dict: createDictFeatures(input.features),
      coinsCost: 0,
      fiatCost: 0,
      fiatFakeCost: 0,
      telegramStarsAmount: 0,
      paidPaymentSystem: undefined,
      skipSummary: input.skipSummary ?? false,
    }),
    initial: 'selecting',

    states: {
      /** Multi (or single) screen selection, grouped by type */
      selecting: {
        on: {
          TOGGLE_FEATURE: {
            actions: assign(({ context, event }) => {
              const id = event.id;
              const elem = context.dict[id];
              if (elem && elem.type === 'TopUp') {
                context.selected = [...context.selected.filter((exisId) => context.dict[exisId].type !== 'TopUp')];
                context.selected = [...context.selected, id];

                return context;
              }

              if (context.selected.includes(id)) {
                context.selected = context.selected.filter((exisId) => exisId !== id);
              } else {
                context.selected = [...context.selected, id];
              }
              return context;
            }),
          },
          NEXT_PAGE: [
            {
              // still have pages left -> move to next page
              guard: ({ context }) => context.typeIndex < context.typesOrder.length - 1,
              actions: assign(({ context }) => {
                context.typeIndex += 1;
                return context;
              }),
            },
            {
              // skip summary -> go directly to checkout
              guard: ({ context }) => context.skipSummary,
              target: 'checkout',
            },
            {
              // no more pages -> jump to summary
              target: 'summary',
            },
          ],
          PREV_PAGE: {
            guard: ({ context }) => context.typeIndex > 0,
            actions: assign(({ context }) => {
              context.typeIndex -= 1;
              return context;
            }),
          },
        },
      },

      /** Summary with total */
      summary: {
        entry: ({ context }) => {
          Object.assign(context, computeTotal(context.selected, context.dict));
        },
        on: {
          PREV_PAGE: 'selecting',
          BACK_TO_SELECTION: 'selecting',
          NEXT_PAGE: 'checkout',
        },
      },

      checkout: {
        entry: ({ context }) => {
          if (context.skipSummary) {
            Object.assign(context, computeTotal(context.selected, context.dict));
          }
        },
        invoke: {
          id: 'featureCheckoutFlow',
          src: createFeatureCheckoutFlow(onSuccessPay),
          input: ({ context }) => ({
            payments: context.payments,
            availableAmounts: context.availableAmounts,
            productId: context.productId,
            coinsBalance: context.coinsBalance,
            selected: context.selected,
            dict: context.dict,
            coinsCost: context.coinsCost,
            fiatCost: context.fiatCost,
            fiatFakeCost: context.fiatFakeCost,
            telegramStarsAmount: context.telegramStarsAmount,
          }),
          onDone: {
            target: 'thankYou',
            actions: assign(({ context, event }) => {
              context.paidPaymentSystem = event.output.paymentSystem;
              return context;
            }),
          },
        },
        on: {
          SELECT_METHOD: { actions: sendTo('featureCheckoutFlow', ({ event }) => event) },
          GO_PAY_CARD_COM: { actions: sendTo('featureCheckoutFlow', ({ event }) => event) },
          MANUAL_TRANSFER: { actions: sendTo('featureCheckoutFlow', ({ event }) => event) },
          NEED_MORE_COINS: { actions: sendTo('featureCheckoutFlow', ({ event }) => event) },
          BACK_TO_SELECTION: { actions: sendTo('featureCheckoutFlow', ({ event }) => event) },
          PREV_PAGE: { actions: sendTo('featureCheckoutFlow', ({ event }) => event) },
          BACK_TO_SUMMARY: [
            {
              guard: ({ context }) => context.skipSummary,
              target: 'selecting',
            },
            {
              target: 'summary',
            },
          ],
        },
      },

      thankYou: {
        type: 'final',
      },
    },
  });
}
